import { ResponseDataType } from '@react/FilterableList/FilterableListTypes'

export const ERROR_CODE_BACKEND_TIMEOUT = 'BACKEND_TIMEOUT'
export const ERROR_CODE_GENERIC_ERROR = 'GENERIC_ERROR'

interface TimeoutHandlerParams {
  errorCode: string
  loadMoreItems: boolean
  itemsState: unknown[]
  responseData: ResponseDataType | null
  setResponseData: (data: ResponseDataType | string | null) => void
  setIsLoading: (loading: boolean) => void
  setShowLoadingSpinnerOnInit: (show: boolean) => void
}

interface ErrorResponseParams {
  response: Response
  loadMoreItems: boolean
  itemsState: unknown[]
  responseData: ResponseDataType | null
  setResponseData: (data: ResponseDataType | string | null) => void
  setIsLoading: (loading: boolean) => void
  setShowLoadingSpinnerOnInit: (show: boolean) => void
}

const getErrorCode = async (response: Response): Promise<string> => {
  const errorJson = await response.json().catch(() => ({}))
  return errorJson?.errorCode || (response.status === 500 ? ERROR_CODE_BACKEND_TIMEOUT : ERROR_CODE_GENERIC_ERROR)
}

export const processErrorResponse = async (params: ErrorResponseParams): Promise<boolean> => {
  const { response, loadMoreItems, itemsState, responseData, setResponseData, setIsLoading, setShowLoadingSpinnerOnInit } = params
  
  const errorCode = await getErrorCode(response)
  
  const handled = handleBackendTimeout({
    errorCode,
    loadMoreItems,
    itemsState,
    responseData,
    setResponseData,
    setIsLoading,
    setShowLoadingSpinnerOnInit,
  })

  if (handled) return true
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${errorCode}`)
  return false
}

export const handleBackendTimeout = (params: TimeoutHandlerParams): boolean => {
  const {
    errorCode,
    loadMoreItems,
    itemsState,
    responseData,
    setResponseData,
    setIsLoading,
    setShowLoadingSpinnerOnInit,
  } = params

  if (errorCode !== ERROR_CODE_BACKEND_TIMEOUT) {
    return false
  }

  const hasExistingItems = itemsState.length > 0 || (responseData?.items && responseData.items.length > 0)
  
  if (loadMoreItems && hasExistingItems) {
    // Load more timeout: preserve existing items and keep button visible
    const existingItems = itemsState.length > 0 ? itemsState : (responseData?.items || [])
    
    setResponseData({
      ...responseData,
      errorCode: ERROR_CODE_BACKEND_TIMEOUT,
      loadMoreError: true,
      items: existingItems,
      loadMore: true,
    } as ResponseDataType)
  } else {
    // Initial load timeout: show full-page error
    setResponseData({
      errorCode: ERROR_CODE_BACKEND_TIMEOUT,
      items: [],
      filterFields: [],
      loadMore: false,
      totalNumber: 0,
    } as ResponseDataType)
  }

  setIsLoading(false)
  setShowLoadingSpinnerOnInit(false)

  return true
}
